import {Injectable} from '@angular/core';
import {AuthService} from "@app/_services/auth.service";
import {
    createBearerAuthenticationConfig,
    MetadataSearchResponse,
    ObjectIDAndName,
    SearchMetadataRequest,
    ThoughtSpotRestApi
} from "@thoughtspot/rest-api-sdk";
import {environment} from "@environments/environment";
import {Liveboard, LiveboardMenuItems, ThoughtSpotLoginResponse} from "@app/_models/thoughtspot";
import {BehaviorSubject, Observable} from "rxjs";
import {AuthType, init, LogLevel} from "@thoughtspot/visual-embed-sdk";
import {ApiService} from "@app/_services/api.service";
import {GetThoughtspotTokenRequest} from "@app/_models/api";

@Injectable(
    {providedIn: 'root'}
)
export class ThoughtspotService {
    private _liveboards: BehaviorSubject<LiveboardMenuItems> = new BehaviorSubject<LiveboardMenuItems>({admin: [], user: [], shared: []});

    constructor(private authService: AuthService, private apiService: ApiService) {
    }

    public liveboards(): Observable<LiveboardMenuItems> {
        return this._liveboards.asObservable();
    }

    public initEmbedSDK() {
        init({
            disableSDKTracking: true,
            thoughtSpotHost: environment.thoughtSpotConfig.apiBaseUrl,
            authType: AuthType.TrustedAuthTokenCookieless,
            queueMultiRenders: true,
            autoLogin: true,
            username: this.authService.userValue!.email!,
            disableLoginRedirect: true,
            shouldEncodeUrlQueryParams: true,
            blockNonEmbedFullAppAccess: true,
            logLevel: LogLevel.SILENT,
            getAuthToken: () => this.getAuthToken(),
        });
    }

    public getLiveboards() {
        const client = new ThoughtSpotRestApi(this.getApiClientConfig());
        const searchRequest: SearchMetadataRequest = {metadata: [{type: "LIVEBOARD"}], record_size: environment.thoughtSpotConfig.defaultRecordSize};

        client.searchMetadata(searchRequest).then((response: MetadataSearchResponse[]) => {
            const adminLiveboards: any[] = [];
            const userLiveboards: any[] = [];
            const sharedLiveboards: any[] = [];

            response.forEach(
                (entity) => {
                    const liveboard: Liveboard = {id: entity.metadata_id!, name: entity.metadata_name!, icon: "fa-search"}
                    if (environment.thoughtSpotConfig.hiddenVisualizations.includes(liveboard.id)) {
                        return;
                    }

                    if (entity.metadata_header.tags.some((tag: ObjectIDAndName) => tag.name === "Admin")) {
                        adminLiveboards.push(liveboard);
                        return;
                    }

                    if (entity.metadata_header.authorName == this.authService.userValue!.email) {
                        userLiveboards.push(liveboard);
                        return;
                    }

                    sharedLiveboards.push(liveboard);
                }
            );

            const updatedLiveboards: LiveboardMenuItems = {
                admin: adminLiveboards.sort((a, b) => (a.name < b.name ? -1 : 1)),
                user: userLiveboards.sort((a, b) => (a.name < b.name ? -1 : 1)),
                shared: sharedLiveboards.sort((a, b) => (a.name < b.name ? -1 : 1)),
            };

            this._liveboards.next(updatedLiveboards);
        });
    }

    public async getLiveboardMetadata(liveboardId: string): Promise<MetadataSearchResponse[]> {
        const tsRestApiClient = new ThoughtSpotRestApi(this.getApiClientConfig());
        const searchRequest: SearchMetadataRequest = {metadata: [{identifier: liveboardId, type: "LIVEBOARD"}]};

        return tsRestApiClient.searchMetadata(searchRequest);
    }

    public async getAnswersMetadataForUser(username: string): Promise<MetadataSearchResponse[]> {
        const tsRestApiClient = new ThoughtSpotRestApi(this.getApiClientConfig());
        const searchRequest: SearchMetadataRequest = {
            metadata: [{type: "ANSWER"}],
            permissions: [{principal: {type: "USER", identifier: username}, share_mode: "READ_ONLY"}],
            record_size: -1,
            record_offset: 0
        };

        return tsRestApiClient.searchMetadata(searchRequest);
    }

    private getApiClientConfig() {
        return createBearerAuthenticationConfig(
            environment.thoughtSpotConfig.apiBaseUrl,
            async () => this.getAuthToken()
        );
    }

    private async getAuthToken(): Promise<string> {
        const requestBody: GetThoughtspotTokenRequest = {email: this.authService.userValue!.email!};
        return this.apiService.getThoughtspotToken(requestBody).then((r: ThoughtSpotLoginResponse) => {
            return r.token;
        });
    }
}
